// The signing certificate each user of this app imported in this browser (T17.1): IndexedDB of the
// app's origin, one record per user ID. A record holds the P12/PFX file exactly as imported, still
// encrypted with its password (checkP12Protection refused it otherwise), and nonsecret details to
// show it by. Never a password, an unlocked key or the key's bytes, and nothing here is sent to the
// server. Other sites cannot read it. Clearing this site's browser data, or another browser or
// device, means importing the file again.

import type { User } from '@/models/User/Model'

const DATABASE = 'racuni-signing-certificates'
const STORE = 'certificates'

export interface StoredSigningCertificate {
	userId: User['id']
	// The imported file, byte for byte.
	bundle: ArrayBuffer
	fileName: string
	byteSize: number
	sha256: string
	importedAt: string
}

export type SigningCertificateDetails = Omit<StoredSigningCertificate, 'bundle'>

function open(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DATABASE, 1)
		request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'userId' })
		request.onsuccess = () => resolve(request.result)
		request.onerror = () => reject(request.error)
		request.onblocked = () => reject(new Error('The certificate store is blocked by another tab.'))
	})
}

// One request in its own transaction; resolves once the transaction has committed.
async function run<T>(
	mode: IDBTransactionMode,
	operate: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
	const database = await open()
	try {
		return await new Promise<T>((resolve, reject) => {
			const transaction = database.transaction(STORE, mode)
			const request = operate(transaction.objectStore(STORE))
			transaction.oncomplete = () => resolve(request.result)
			transaction.onerror = () => reject(transaction.error ?? request.error)
			transaction.onabort = () => reject(transaction.error ?? new Error('Transaction aborted.'))
		})
	} finally {
		database.close()
	}
}

export async function readSigningCertificate(
	userId: User['id'],
): Promise<StoredSigningCertificate | null> {
	return (await run('readonly', (store) => store.get(userId))) ?? null
}

/** Stores the user's certificate, in place of the one stored before. */
export async function saveSigningCertificate(record: StoredSigningCertificate): Promise<void> {
	await run('readwrite', (store) => store.put(record))
}

export async function removeSigningCertificate(userId: User['id']): Promise<void> {
	await run('readwrite', (store) => store.delete(userId))
}
